import { reactive } from 'vue';

/**
 * DEMANDER CONFIRMATION, DANS LA CHARTE.
 *
 * Un geste irréversible (supprimer un brouillon, exporter en paie) ne passe
 * JAMAIS par le `confirm()` du navigateur : une boîte grise du système, aux
 * boutons « OK / Annuler » parfois en anglais, qui a l'air d'une erreur de
 * l'application au moment précis où l'on veut inspirer confiance.
 *
 * L'API RESTE IMPÉRATIVE, exprès : `if (!(await confirmer({...}))) return;` se
 * lit comme un `confirm()`, sans état ni gabarit à recopier dans chaque écran —
 * autant d'occasions de diverger là où il n'y a qu'une question à poser.
 */
export interface DemandeConfirmation {
  /** Titre de la fenêtre : le geste, à l'infinitif. */
  titre: string;
  /** La question, en une ou deux phrases. */
  message: string;
  /** Ce que le geste emporte ou change, ligne par ligne. */
  details?: string[];
  /** Le libellé du bouton qui agit. Il NOMME le geste, jamais « OK ». */
  action?: string;
  /** `true` (défaut) : geste destructeur, bouton rouge. `false` : geste neutre. */
  danger?: boolean;
  /** La voie douce, quand elle existe. */
  repli?: string;
}

interface Etat extends DemandeConfirmation {
  ouvert: boolean;
  resoudre: ((ok: boolean) => void) | null;
}

/** Lu par `ConfirmerHote`, monté une seule fois dans la coquille. */
export const etatConfirmation = reactive<Etat>({
  ouvert: false,
  titre: '',
  message: '',
  details: [],
  action: 'Confirmer',
  danger: true,
  repli: '',
  resoudre: null,
});

export function confirmer(demande: DemandeConfirmation): Promise<boolean> {
  // Une demande qui en interrompt une autre annule la précédente : laisser deux
  // promesses en suspens, c'est laisser un appelant attendre pour toujours.
  etatConfirmation.resoudre?.(false);

  etatConfirmation.titre = demande.titre;
  etatConfirmation.message = demande.message;
  etatConfirmation.details = demande.details ?? [];
  etatConfirmation.action = demande.action ?? 'Confirmer';
  etatConfirmation.danger = demande.danger ?? true;
  etatConfirmation.repli = demande.repli ?? '';
  etatConfirmation.ouvert = true;

  return new Promise<boolean>((resolve) => {
    etatConfirmation.resoudre = resolve;
  });
}

/** Referme et rend la réponse. Appelé par l'hôte, et par lui seul. */
export function repondre(ok: boolean): void {
  etatConfirmation.ouvert = false;
  const resoudre = etatConfirmation.resoudre;
  etatConfirmation.resoudre = null;
  resoudre?.(ok);
}
